/**
 * Version-skew guard: long-lived practice tabs keep running stale SPA bundles
 * against newer backends (T-1724 lost generate opt-in, T-2197 generate 400s).
 * The build emits the bundle version to /version.json (vite.config.ts); this
 * module compares it against the running bundle's REACT_APP_VERSION and forces
 * a full reload at the next completed route navigation — never mid-flow.
 */

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export const DEPLOYED_VERSION_URL = "/version.json";

/**
 * Best-effort by contract: any transport or shape failure (offline, 404, SPA
 * fallback HTML from a deploy predating version.json) returns null, meaning
 * "unknown — keep the current bundle". The guard only ever reloads on a
 * positive version mismatch.
 */
export const fetchDeployedVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch(DEPLOYED_VERSION_URL, { cache: "no-store" });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (typeof body === "object" && body !== null && "version" in body) {
      const { version } = body as { version: unknown };
      if (typeof version === "string" && version.length > 0) return version;
    }
    return null;
  } catch {
    return null;
  }
};

type VersionSkewGuardDeps = {
  bundleVersion: string;
  initialPathname: string;
  fetchDeployedVersion: () => Promise<string | null>;
  reload: () => void;
  now: () => number;
  checkIntervalMs?: number;
};

export const createVersionSkewGuard = ({
  bundleVersion,
  initialPathname,
  fetchDeployedVersion,
  reload,
  now,
  checkIntervalMs = CHECK_INTERVAL_MS,
}: VersionSkewGuardDeps) => {
  let lastPathname = initialPathname;
  let reloadPending = false;
  let nextCheckAt = 0;

  // Both entry points hand back the in-flight check so a caller can await it;
  // the browser-facing call sites deliberately fire and forget.
  const maybeCheck = (): Promise<void> => {
    if (reloadPending || now() < nextCheckAt) return Promise.resolve();
    nextCheckAt = now() + checkIntervalMs;
    return fetchDeployedVersion().then((deployedVersion) => {
      if (deployedVersion !== null && deployedVersion !== bundleVersion) {
        reloadPending = true;
      }
    });
  };

  // A completed navigation is the one safe moment to swap bundles: the user
  // just landed and has no in-progress work on the new route yet. Detection is
  // async and only arms the flag; the reload itself always happens here.
  const handleNavigationCompleted = (pathname: string): Promise<void> => {
    // Search, filter, and hash edits are still the same page: never interrupt typing.
    const changedPage = pathname !== lastPathname;
    lastPathname = pathname;
    if (changedPage && reloadPending) {
      reload();
      return Promise.resolve();
    }
    return maybeCheck();
  };

  return { handleNavigationCompleted, maybeCheck };
};

type RouterState = { navigation: { state: string }; location: { key: string; pathname: string } };
type RouterLike = {
  state: RouterState;
  subscribe: (listener: (state: RouterState) => void) => () => void;
};

export const installVersionSkewGuard = (router: RouterLike): void => {
  const bundleVersion = import.meta.env.REACT_APP_VERSION;
  if (!bundleVersion) return; // dev/test builds have no version to compare against

  const guard = createVersionSkewGuard({
    bundleVersion,
    initialPathname: router.state.location.pathname,
    fetchDeployedVersion,
    reload: () => globalThis.location.reload(),
    now: Date.now,
  });

  let lastLocationKey = router.state.location.key;
  router.subscribe((state) => {
    if (state.navigation.state !== "idle" || state.location.key === lastLocationKey) return;
    lastLocationKey = state.location.key;
    void guard.handleNavigationCompleted(state.location.pathname);
  });

  // Long-idle tabs (overnight kiosks) refresh the staleness signal on return,
  // so their first navigation back can already swap to the new bundle.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void guard.maybeCheck();
  });
};
